const API_BASE = '/api'

// Auth token getter — set by the app when Clerk is available
let getAuthToken: (() => Promise<string | null>) | null = null

export function setAuthTokenGetter(getter: () => Promise<string | null>) {
  getAuthToken = getter
}

/**
 * Resolve the current Clerk token, or null when signed out / unavailable.
 * Never throws — callers (e.g. fire-and-forget analytics) can safely use this
 * to no-op when the user is not signed in.
 */
export async function getAuthTokenOrNull(): Promise<string | null> {
  if (!getAuthToken) return null
  try {
    return await getAuthToken()
  } catch {
    return null
  }
}

/** Authenticated fetch wrapper -- attaches Clerk JWT to all requests */
export async function apiFetch(path: string, options?: RequestInit): Promise<Response> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options?.headers as Record<string, string> || {}),
  }

  if (getAuthToken) {
    const token = await getAuthToken()
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }
  }

  return fetch(`${API_BASE}${path}`, { ...options, headers })
}

interface PreviousCard {
  name: string
  orientation: 'upright' | 'reversed'
  interpretation: string
}

interface InterpretRequest {
  card: {
    id: string
    name: string
    description: string
    kind?: 'tarot' | 'oracle'
    // Tarot-only fields. Oracle decks (kind === 'oracle') omit these and
    // the backend takes a different prompt branch.
    arcana?: string
    suit?: string
    element?: string
    keywords?: { upright: string[]; reversed: string[] }
  }
  orientation: 'upright' | 'reversed'
  question?: string
  previousCards?: PreviousCard[]
  readingId?: string
}

/** Create a new reading session in the DB -- returns the readingId */
export async function createReadingSession(question?: string, deckId?: string): Promise<string | null> {
  try {
    const res = await apiFetch('/readings', {
      method: 'POST',
      body: JSON.stringify({ question, deckId }),
    })
    if (!res.ok) return null
    const data = await res.json()
    return data.reading?.id || null
  } catch {
    return null
  }
}

/** Fetch reading history */
export async function fetchReadingHistory() {
  const res = await apiFetch('/readings')
  if (!res.ok) return []
  const data = await res.json()
  return data.readings || []
}

export interface UserRsvp {
  id: string
  ceremonyId: string
  title: string
  description: string | null
  scheduledAt: string
  isPaidOnly: boolean
  rsvpedAt: string
  platform?: 'zoom' | 'youtube'
  accessUrl?: string | null
  accessCode?: string | null
  durationMinutes?: number | null
  recordingUrl?: string | null
}

/** Fetch the current user's ceremony RSVPs (upcoming + past) */
export async function fetchMyRsvps(): Promise<UserRsvp[]> {
  const res = await apiFetch('/ceremonies/me/rsvps')
  if (!res.ok) return []
  const data = await res.json()
  return data.rsvps || []
}

/** Cancel an RSVP for a ceremony */
export async function cancelRsvp(ceremonyId: string): Promise<boolean> {
  const res = await apiFetch(`/ceremonies/${ceremonyId}/rsvp`, { method: 'DELETE' })
  return res.ok
}

// Every onError string is shown to the user as their reading's ending — it
// must always be human words (the server's own message when it sent one),
// never a status code or exception text.
const READING_CONNECTION_ERROR = 'The Cosmic Finder lost connection. Please try again.'

export async function streamReading(
  request: InterpretRequest,
  onToken: (token: string) => void,
  onDone: () => void,
  onError: (error: string) => void,
  signal?: AbortSignal,
) {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }

    if (getAuthToken) {
      const token = await getAuthToken()
      if (token) {
        headers['Authorization'] = `Bearer ${token}`
      }
    }

    const response = await fetch(`${API_BASE}/readings/interpret`, {
      method: 'POST',
      headers,
      body: JSON.stringify(request),
      signal,
    })

    if (!response.ok) {
      // Limit (429) and tier (403) rejections carry a user-ready explanation
      // in the JSON body; surfacing it is the difference between "you've used
      // your free pulls" and an inexplicable dead reading.
      const body = await response.json().catch(() => null)
      onError(typeof body?.error === 'string' && body.error.trim() ? body.error : READING_CONNECTION_ERROR)
      return
    }

    const reader = response.body?.getReader()
    if (!reader) {
      onError(READING_CONNECTION_ERROR)
      return
    }

    const decoder = new TextDecoder()
    let buffer = ''

    while (true) {
      const { done, value } = await reader.read()
      if (done) break

      buffer += decoder.decode(value, { stream: true })
      const lines = buffer.split('\n')
      buffer = lines.pop() || ''

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          try {
            const data = JSON.parse(line.slice(6))
            // The server reports failures as an {error} event followed by
            // {done} on the same stream — the error must win, or a broken
            // reading gets presented as complete.
            if (typeof data.error === 'string' && data.error.trim()) {
              onError(data.error)
              return
            }
            if (data.done) {
              onDone()
              return
            }
            if (data.token) {
              onToken(data.token)
            }
          } catch {
            // skip malformed JSON lines
          }
        }
      }
    }

    onDone()
  } catch (err) {
    // Caller-initiated aborts are not errors — they happen when a newer
    // stream supersedes this one (e.g. user draws another card mid-read).
    if (err instanceof DOMException && err.name === 'AbortError') return
    onError(READING_CONNECTION_ERROR)
  }
}
